import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FieldProps, WidgetProps } from '@rjsf/utils';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import { useTranslation } from 'react-i18next';
import {
  buttonClasses,
  inputClasses,
  buttonRowClasses,
} from 'styles/tailwindClassnames';
import { colors } from 'styles/theme/colors';

import {
  Box,
  BoxField as Field,
  BoxInputs,
  INVALID_BOX_MESSAGE,
  boxFromBounds,
  isValidBox,
  parseBox,
  parseInputs,
  round,
  serializeBox,
  serializeInputs,
} from './boundingBox';
import {
  IMPORT_POLYGON_MESSAGE,
  Polygon,
  boxToPolygon,
  isRectangle,
  isValidPolygon,
  parsePolygon,
  polygonFromLatLngs,
  polygonToBox,
  serializePolygon,
  TYPED_EDGES_KEY,
  typedEdgesOf,
} from './polygon';

// One map widget for every area a connector asks for:
// - "bbox": a rectangle only, stored as the four numbers
//   "[ne_lat, ne_lng, sw_lat, sw_lng]" (see boundingBox.ts). Drawing a polygon
//   and importing GeoJSON are greyed out.
// - "polygon": a rectangle or a polygon, stored as a GeoJSON Polygon
//   (see polygon.ts).
// While the area is a rectangle (or empty), its edges can also be typed in.
export type MapAreaMode = 'bbox' | 'polygon';

const SHAPE_COLOR = colors['teal-t100'];
const PATH_STYLE = { color: SHAPE_COLOR, weight: 2 };

type Shape = 'Rectangle' | 'Polygon';

// Display order: latitudes first (north above south), then longitudes (east, west).
const FIELDS = [
  { key: 'north', label: 'Max Latitude (N)' },
  { key: 'south', label: 'Min Latitude (S)' },
  { key: 'east', label: 'Max Longitude (E)' },
  { key: 'west', label: 'Min Longitude (W)' },
] as const;

const EMPTY_INPUTS: BoxInputs = { north: '', south: '', east: '', west: '' };

// Reads the four inputs by name (not position), or null if any is empty or not a number.
const boxFromInputs = (inputs: BoxInputs): Box | null =>
  parseBox(serializeInputs(inputs));

const inputsFromBox = (b: Box): BoxInputs => ({
  north: String(round(b.north)),
  south: String(round(b.south)),
  east: String(round(b.east)),
  west: String(round(b.west)),
});

// Leaflet returns a polygon's corners as rings (or nested rings for a
// multipolygon, which the widget never creates).
const ringsOf = (layer: L.Polygon): L.LatLng[][] => {
  const latlngs = layer.getLatLngs() as any[];
  return Array.isArray(latlngs[0]) ? latlngs : [latlngs];
};

const boundsOf = (p: Polygon) =>
  L.latLngBounds(p.coordinates[0].map(([lng, lat]) => [lat, lng]));

type MapAreaWidgetProps = WidgetProps & { mode: MapAreaMode };

const MapAreaWidget: React.FC<MapAreaWidgetProps> = ({
  mode,
  value,
  onChange,
  disabled,
  readonly,
  label,
  schema,
  uiSchema,
}) => {
  // fullWidth fields get no label from the field template, so the widget draws
  // its own header. `label` is the ui:title node from generateUISchema.
  const description =
    (uiSchema?.['ui:description'] as React.ReactNode) ?? schema?.description;
  const { t, i18n } = useTranslation();
  const editable = !disabled && !readonly;
  const boxOnly = mode === 'bbox';
  const asObject = !boxOnly && schema?.type === 'object';

  // The area on the map, as a polygon in both modes. An invalid value is typed
  // text the user is still fixing, shown in the inputs only.
  const shapeOf = (v: unknown): Polygon | null => {
    if (boxOnly) {
      const b = parseBox(v);
      return b && isValidBox(b) ? boxToPolygon(b) : null;
    }
    const p = parsePolygon(v);
    if (p && isValidPolygon(p)) return p;
    // A box saved while the field still asked for "bbox" shows as a
    // rectangle; the next edit saves it as GeoJSON.
    const b = parseBox(v);
    return b && isValidBox(b) ? boxToPolygon(b) : null;
  };
  // Parsed and validated once per value: the widget re-renders on every mouse
  // move (the cursor read-out), and checking a large polygon is not free.
  const keyOf = (v: unknown) =>
    typeof v === 'string' ? v : JSON.stringify(v ?? '');
  const valueKey = keyOf(value);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const area = useMemo(() => shapeOf(value), [valueKey, boxOnly]);
  const areaIsRectangle = useMemo(() => !!area && isRectangle(area), [area]);
  const inputsOf = (v: unknown): BoxInputs => {
    if (boxOnly) return parseInputs(v);
    // Edges typed but not a valid box yet (see polygon.ts, typedEdgesOf).
    const typed = typedEdgesOf(v);
    if (typed) return parseInputs(typed);
    const p = shapeOf(v);
    return p && isRectangle(p) ? inputsFromBox(polygonToBox(p)) : EMPTY_INPUTS;
  };

  const mapEl = useRef<HTMLDivElement>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true; // again after React's dev-mode remount
    return () => {
      mounted.current = false;
    };
  }, []);
  const mapRef = useRef<L.Map | null>(null);
  const shapeRef = useRef<L.Polygon | null>(null);
  const [drawShape, setDrawShape] = useState<Shape | null>(null);
  const [cursor, setCursor] = useState<L.LatLng | null>(null);
  const [inputs, setInputs] = useState<BoxInputs>(() => inputsOf(value));
  const [importOpen, setImportOpen] = useState(false);
  // The edge inputs only describe a rectangle: they hide for a polygon on the
  // map, and as soon as the user starts drawing a polygon or opens the import.
  const showInputs =
    boxOnly ||
    (drawShape !== 'Polygon' && !importOpen && (!area || areaIsRectangle));
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState(false);

  // Handlers registered once on the map read the latest props through this ref.
  const latest = useRef({ onChange, boxOnly, asObject, editable });
  latest.current = { onChange, boxOnly, asObject, editable };

  // The stored form of an area, or null when it is not valid for this mode.
  const serialize = (p: Polygon): string | Polygon | null => {
    const { boxOnly: box, asObject: obj } = latest.current;
    if (box) {
      const b = polygonToBox(p);
      return isValidBox(b) ? serializeBox(b) : null;
    }
    return isValidPolygon(p) ? serializePolygon(p, obj) : null;
  };

  // A rejected area (zero size, fewer than three corners) must put the map
  // back to the saved value, so the map never disagrees with the inputs.
  const [resync, setResync] = useState(0);
  const commit = useCallback((p: Polygon) => {
    const v = serialize(p);
    if (v !== null) latest.current.onChange(v);
    else setResync((n) => n + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the text inputs in step with the stored value (map edits, Clear, reload).
  // Not for the value the user just typed: that would swap their text for its
  // normalized number mid-edit ("-1." becoming "-1", losing the decimal point).
  const typedKey = useRef<string | null>(null);
  useEffect(() => {
    if (valueKey === typedKey.current) return;
    typedKey.current = null;
    setInputs(inputsOf(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  // Map setup (once).
  useEffect(() => {
    if (!mapEl.current) return;
    const map = L.map(mapEl.current, {
      center: [0, 20],
      zoom: 2,
      worldCopyJump: true,
    });
    mapRef.current = map;
    L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
      { attribution: 'Tiles © Esri', maxZoom: 16 },
    ).addTo(map);
    map.on('mousemove', (e: L.LeafletMouseEvent) => setCursor(e.latlng));
    map.on('mouseout', () => setCursor(null));

    map.pm.setGlobalOptions({
      allowSelfIntersection: false,
      pathOptions: PATH_STYLE,
      templineStyle: PATH_STYLE,
      hintlineStyle: { ...PATH_STYLE, dashArray: '5,5' },
    } as any);
    // Double-clicking to close a polygon sends Geoman two clicks. Unless the
    // second lands on exactly the same spot, it adds one more corner right
    // next to the first, and Geoman can then refuse to close the shape. These
    // handlers are registered before any drawing starts, so they run before
    // Geoman's: note the corner count as the second click arrives, and if
    // that click added a corner, remove it before the double-click closes the
    // shape. Corners placed with separate clicks are never touched.
    const polygonDraw = () => {
      const draw = (map.pm.Draw as any).Polygon;
      return draw?._enabled ? draw : null;
    };
    let cornersBeforeSecondClick: number | null = null;
    map.on('click', (e: L.LeafletMouseEvent) => {
      const draw = polygonDraw();
      cornersBeforeSecondClick =
        draw && e.originalEvent?.detail === 2
          ? draw._layer.getLatLngs().length
          : null;
    });
    map.on('dblclick', () => {
      const draw = polygonDraw();
      if (
        draw &&
        cornersBeforeSecondClick !== null &&
        draw._layer.getLatLngs().length === cornersBeforeSecondClick + 1
      )
        draw._removeLastVertex();
      cornersBeforeSecondClick = null;
    });
    // The drawn layer is only a draft: the saved value is what gets shown.
    map.on('pm:create', (e: any) => {
      e.layer.remove();
      if (!latest.current.editable) return;
      commit(polygonFromLatLngs(ringsOf(e.layer)));
    });
    map.on('pm:drawend', () => setDrawShape(null));

    return () => {
      map.remove();
      mapRef.current = null;
      shapeRef.current = null;
    };
  }, [commit]);

  // Geoman's drawing hints follow the portal language.
  useEffect(() => {
    const lang = (i18n.language || 'en').slice(0, 2);
    if (['en', 'es', 'fr'].includes(lang))
      mapRef.current?.pm.setLang(lang as any);
  }, [i18n.language]);

  // Render the stored area, editable by its corners. A rectangle stays a
  // rectangle while its corners are dragged.
  const areaKey = useMemo(() => (area ? JSON.stringify(area) : ''), [area]);
  const fitted = useRef(false);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    shapeRef.current?.pm.disable();
    shapeRef.current?.remove();
    shapeRef.current = null;
    // Starting a new area hides the current one until drawing ends.
    if (!area || drawShape) return;
    const shape = isRectangle(area)
      ? L.rectangle(boundsOf(area), PATH_STYLE)
      : L.polygon(
          area.coordinates.map((ring) =>
            ring.slice(0, -1).map(([lng, lat]) => [lat, lng]),
          ) as L.LatLngExpression[][],
          PATH_STYLE,
        );
    shape.addTo(map);
    shapeRef.current = shape;
    if (!fitted.current) {
      map.fitBounds(boundsOf(area), { padding: [30, 30] });
      fitted.current = true;
    }
    if (!editable) return;
    shape.pm.enable({ allowSelfIntersection: false, draggable: true } as any);
    shape.on('pm:edit', () => commit(polygonFromLatLngs(ringsOf(shape))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaKey, editable, resync, drawShape]);

  // Locking the field (its action switched off, or the form read-only) ends any
  // drawing in progress and closes the import panel, so neither can change the
  // locked value.
  useEffect(() => {
    if (editable) return;
    mapRef.current?.pm.disableDraw();
    setDrawShape(null);
    setImportOpen(false);
  }, [editable]);

  const toggleDraw = (shape: Shape) => {
    const map = mapRef.current;
    if (!map) return;
    map.pm.disableDraw();
    // One way of setting the area at a time: drawing closes the import panel.
    setImportOpen(false);
    setImportError(false);
    if (drawShape === shape) {
      setDrawShape(null);
      return;
    }
    // A double-click closes a polygon (once it has three corners).
    map.pm.enableDraw(
      shape,
      shape === 'Polygon' ? ({ finishOn: 'dblclick' } as any) : undefined,
    );
    setDrawShape(shape);
  };

  const useMapView = () => {
    const map = mapRef.current;
    const b = map?.getBounds();
    if (!b) return;
    map?.pm.disableDraw();
    setImportOpen(false);
    // The area is the view itself, so the map stays put. Without this, the
    // first area on the map zooms out to fit with padding and the rectangle
    // ends up covering only part of the view.
    fitted.current = true;
    commit(boxToPolygon(boxFromBounds(b)));
  };

  const clear = () => {
    mapRef.current?.pm.disableDraw();
    setDrawShape(null);
    onChange(undefined);
  };

  const onInput = (key: Field, text: string) => {
    const next = { ...inputs, [key]: text };
    setInputs(next);
    const emit = (v: unknown) => {
      typedKey.current = keyOf(v);
      onChange(v);
    };
    const b = boxFromInputs(next);
    const valid = b && isValidBox(b) ? serialize(boxToPolygon(b)) : null;
    if (b && valid !== null) {
      emit(valid);
      mapRef.current?.fitBounds(
        [
          [b.south, b.west],
          [b.north, b.east],
        ],
        { padding: [30, 30] },
      );
    } else if (FIELDS.every((f) => next[f.key].trim() === '')) {
      emit(undefined);
    } else {
      // Kept exactly as typed, so a form re-render (Save adding its errors) shows
      // the user's text again rather than the last valid area, and so the save
      // validators see it and refuse to save. An object field keeps it in an
      // object, so the field's type stays what the schema says.
      emit(
        asObject
          ? {
              [TYPED_EDGES_KEY]: [next.north, next.east, next.south, next.west],
            }
          : serializeInputs(next),
      );
    }
  };

  const inputsInvalid =
    FIELDS.some((f) => inputs[f.key].trim() !== '') &&
    (() => {
      const b = boxFromInputs(inputs);
      return !b || !isValidBox(b);
    })();

  const applyImport = (text: string) => {
    if (!latest.current.editable) return;
    const p = parsePolygon(text);
    if (!p || !isValidPolygon(p)) {
      setImportError(true);
      return;
    }
    setImportError(false);
    setImportOpen(false);
    setImportText('');
    onChange(serializePolygon(p, asObject));
    mapRef.current?.fitBounds(boundsOf(p), { padding: [30, 30] });
  };

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    file
      .text()
      .then((text) => {
        // The section may have been left while a large file was read.
        if (!mounted.current) return;
        setImportText(text);
        applyImport(text);
      })
      .catch(() => {
        if (mounted.current) setImportError(true);
      });
  };

  const boxOnlyHint = boxOnly
    ? t('This connection only accepts a rectangle.')
    : undefined;

  // lg on phones, where they're tapped. From md up they're sm and always share
  // one row. At any size a label too long for its cell (a longer translation,
  // a narrow card) wraps inside its button instead of spilling out. The `!`
  // beats the shared button's fixed height and nowrap, which Tailwind would
  // otherwise write later in the CSS.
  const mdSm = '!h-full min-h-12 !whitespace-normal py-1 md:min-h-8 md:px-2';
  const btnPrimary = `${buttonClasses.outlinePrimaryAction} ${mdSm}`;
  const btnNeutral = `${buttonClasses.cancelAction} ${mdSm}`;

  return (
    <div
      className="flex w-full flex-col gap-3"
      data-testid="MapAreaWidget-container"
    >
      {(label || description) && (
        <div className="flex flex-col gap-1">
          {label && <div className="text-ui-text-primary-text">{label}</div>}
          {description && (
            <span className="text-body-sm text-ui-text-primary-text">
              {description}
            </span>
          )}
        </div>
      )}
      {/* Equal-width cells. Below md they wrap, at a width that fits the lg
          labels; from md up all five share one row. */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(9.5rem,1fr))] gap-2 md:grid-cols-5 [&>*]:w-full">
        {(['Rectangle', 'Polygon'] as Shape[]).map((shape) => {
          const off = shape === 'Polygon' && boxOnly;
          // The hint sits on a wrapper: disabled buttons get no hover events
          // in some browsers, so a title on the button itself may never show.
          return (
            <span key={shape} title={off ? boxOnlyHint : undefined}>
              <button
                type="button"
                data-testid={`MapAreaWidget-draw${shape}`}
                disabled={!editable || off}
                aria-pressed={drawShape === shape}
                onClick={() => toggleDraw(shape)}
                // While drawing, the button holds its hover look.
                className={`${btnPrimary} w-full aria-pressed:border-teal-ai2-dark-teal aria-pressed:bg-teal-t10 aria-pressed:text-teal-ai2-dark-teal`}
              >
                {shape === 'Rectangle'
                  ? t('Draw Rectangle')
                  : t('Draw Polygon')}
              </button>
            </span>
          );
        })}
        <span title={boxOnlyHint}>
          <button
            type="button"
            data-testid="MapAreaWidget-import"
            disabled={!editable || boxOnly}
            aria-expanded={importOpen}
            onClick={() => {
              // Opening the import ends any drawing in progress.
              if (!importOpen) mapRef.current?.pm.disableDraw();
              setImportOpen((o) => !o);
              setImportError(false);
            }}
            className={`${btnPrimary} w-full`}
          >
            {t('Import GeoJSON')}
          </button>
        </span>
        <button
          type="button"
          data-testid="MapAreaWidget-mapview"
          disabled={!editable}
          onClick={useMapView}
          className={btnPrimary}
        >
          {t('Use Map View')}
        </button>
        <button
          type="button"
          data-testid="MapAreaWidget-clear"
          disabled={!editable || !value}
          onClick={clear}
          className={btnNeutral}
        >
          {t('Clear')}
        </button>
      </div>

      {importOpen && !boxOnly && editable && (
        <div className="flex flex-col gap-2">
          <textarea
            data-testid="MapAreaWidget-importText"
            rows={5}
            value={importText}
            placeholder={t('Paste GeoJSON here')}
            onChange={(e) => {
              setImportText(e.target.value);
              setImportError(false);
            }}
            className={`${importError ? inputClasses.textareaError : inputClasses.textarea} font-mono text-body-sm`}
          />
          {importError && (
            <p
              className="text-body-sm text-ui-status-error-destructive"
              data-testid="MapAreaWidget-importError"
            >
              {t(IMPORT_POLYGON_MESSAGE as any)}
            </p>
          )}
          <div className={buttonRowClasses.row}>
            <button
              type="button"
              data-testid="MapAreaWidget-importApply"
              disabled={!importText.trim()}
              onClick={() => applyImport(importText)}
              className={buttonClasses.outlinePrimaryAction}
            >
              {t('Apply')}
            </button>
            <label
              className={`${buttonClasses.cancelActionLink} cursor-pointer focus-within:outline focus-within:outline-2 focus-within:outline-brand-accent-green`}
            >
              {t('Upload GeoJSON File')}
              <input
                type="file"
                accept=".geojson,.json,application/geo+json,application/json"
                data-testid="MapAreaWidget-importFile"
                onChange={onFile}
                className="sr-only"
              />
            </label>
            <button
              type="button"
              onClick={() => {
                setImportOpen(false);
                setImportError(false);
              }}
              className={buttonClasses.cancelAction}
            >
              {t('Cancel')}
            </button>
          </div>
        </div>
      )}

      {/* `isolate` keeps Leaflet's own z-indexes (panes up to 700, controls
          800+) inside this box, so they never paint over the rest of the
          page, and lets the wash and the readout sit between the panes and
          the controls. */}
      <div className="relative isolate">
        <div
          ref={mapEl}
          data-testid="MapAreaWidget-map"
          className="h-[360px] w-full rounded border border-ui-field-outline"
        />
        {/* Read-only: a light gray wash over the tiles and shapes, like a
            read-only text box. It lets clicks through, so the map still pans
            and zooms for viewing; the zoom buttons stay above it. */}
        {!editable && (
          <div
            data-testid="MapAreaWidget-readonly-wash"
            className="pointer-events-none absolute inset-0 z-[750] rounded bg-greyscale-11 opacity-60"
          />
        )}
        {cursor && (
          <div className="pointer-events-none absolute bottom-6 left-2 z-[760] rounded bg-white px-2 py-1 font-mono text-body-sm shadow">
            {cursor.lat.toFixed(6)}°, {cursor.lng.toFixed(6)}°
          </div>
        )}
      </div>

      {showInputs && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {FIELDS.map((f) => (
              <label
                key={f.key}
                className="flex flex-col gap-1 text-body-sm font-normal text-ui-text-primary-text"
              >
                {t(f.label)}
                <input
                  type="text"
                  inputMode="decimal"
                  disabled={!editable}
                  data-testid={`MapAreaWidget-${f.key}`}
                  value={inputs[f.key]}
                  onChange={(e) => onInput(f.key, e.target.value)}
                  className={`${inputsInvalid ? inputClasses.error : inputClasses.standard} font-normal`}
                />
              </label>
            ))}
          </div>
          {inputsInvalid && (
            <p
              className="text-body-sm text-ui-status-error-destructive"
              data-testid="MapAreaWidget-error"
            >
              {t(INVALID_BOX_MESSAGE as any)}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export const BoxAreaWidget: React.FC<WidgetProps> = (props) => (
  <MapAreaWidget {...props} mode="bbox" />
);

export const PolygonAreaWidget: React.FC<WidgetProps> = (props) => (
  <MapAreaWidget {...props} mode="polygon" />
);

// rjsf ignores ui:widget on an object field, so a connector that stores the
// polygon as an object (not a JSON string) gets the widget as its field.
// onChange needs no field id here: rjsf's SchemaField wraps every field's
// onChange and adds the id itself, so edits still mark the section changed.
export const PolygonAreaField: React.FC<FieldProps> = (props) => (
  <MapAreaWidget
    {...(props as any)}
    mode="polygon"
    value={props.formData}
    label={props.uiSchema?.['ui:title']}
  />
);

export default MapAreaWidget;
